import GridViewIcon from "@mui/icons-material/GridView";
import MenuIcon from "@mui/icons-material/Menu";
import StyleIcon from "@mui/icons-material/Style";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { createUseStyles } from "react-jss";
import { useAtom } from "jotai";
import { answeredState } from "../helpers/recoil";
import { ViewMode } from "../helpers/types";

const useStyles = createUseStyles({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "10px 12px",
    paddingTop: "max(10px, env(safe-area-inset-top))",
    backgroundColor: "#1e2229",
    borderBottom: "1px solid #404754",
    position: "sticky",
    top: 0,
    zIndex: 10
  },
  progress: {
    flex: 1,
    minWidth: 0,
    textAlign: "left"
  },
  progressLabel: {
    fontSize: 13,
    color: "#94a3b8",
    margin: 0
  },
  progressValue: {
    fontSize: 16,
    fontWeight: 600,
    color: "#e2e8f0",
    margin: "2px 0 0"
  },
  score: {
    textAlign: "right",
    minWidth: 64
  },
  scoreValue: {
    fontSize: 16,
    fontWeight: 600,
    color: "#5DCAA5",
    margin: 0
  },
  scoreLabel: {
    fontSize: 12,
    color: "#94a3b8",
    margin: "2px 0 0"
  },
  viewToggle: {
    flexShrink: 0,
    "& .MuiToggleButton-root": {
      color: "#94a3b8",
      borderColor: "#404754",
      padding: "4px 8px",
      "&.Mui-selected": {
        color: "#e2e8f0",
        backgroundColor: "#404754"
      }
    }
  }
});

const FlipCardIcon = () => (
  <SvgIcon
    fontSize="small"
    viewBox="0 0 24 24"
    inheritViewBox
    sx={{ fill: "none" }}>
    <rect
      x="3.25"
      y="4.5"
      width="11"
      height="15"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M6.1 9.4h5.2M6.1 12h5.2M6.1 14.6h3.4"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
    />
    <path
      d="M16.6 8.1c2.2.95 3.35 2.4 3.35 4.15 0 2.35-1.95 4.25-4.55 4.25"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <path
      d="m14.2 6.15 3.15.15-.85 2.95"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

export const FlipAllButton = ({
  allReversed,
  onFlipAll,
  disabled = false,
  className
}: {
  allReversed: boolean;
  onFlipAll: () => void;
  disabled?: boolean;
  className?: string;
}) => {
  const label = allReversed ? "Flip all to front" : "Flip all to back";

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          className={className}
          aria-label={label}
          onClick={onFlipAll}
          disabled={disabled}
          size="small"
          sx={className ? undefined : { color: "#e2e8f0", flexShrink: 0 }}>
          <FlipCardIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const StudyHeader = ({
  viewMode,
  setViewMode,
  cardPosition,
  totalCards,
  onOpenMenu,
  isBrowseMode = false,
  showViewToggle = true,
  title,
  showCount = true
}: {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  cardPosition: number;
  totalCards: number;
  onOpenMenu: () => void;
  isBrowseMode?: boolean;
  showViewToggle?: boolean;
  title?: string;
  showCount?: boolean;
}) => {
  const classes = useStyles();
  const [answeredList] = useAtom(answeredState);
  const correct = answeredList.filter((a) => a.answer === true).length;
  const total = answeredList.length;
  const pct = total > 0 ? Math.round((correct / total) * 100) : null;
  const isGrid = viewMode === "grid";

  return (
    <header className={classes.header}>
      <div className={classes.progress}>
        <p className={classes.progressLabel}>{title ?? (isGrid ? "Grid" : "Card")}</p>
        {showCount ? (
          <p className={classes.progressValue}>
            {isGrid || isBrowseMode
              ? totalCards > 0
                ? `${totalCards} cards`
                : "—"
              : totalCards > 0
                ? `${cardPosition} / ${totalCards}`
                : "—"}
          </p>
        ) : null}
      </div>
      {total > 0 && (
        <div className={classes.score}>
          <p className={classes.scoreValue}>
            {correct}/{total}
          </p>
          <p className={classes.scoreLabel}>{pct}% correct</p>
        </div>
      )}
      {showViewToggle ? (
        <ToggleButtonGroup
          className={classes.viewToggle}
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, value: ViewMode | null) => value && setViewMode(value)}
          aria-label="View mode">
          <ToggleButton value="study" aria-label="Study view">
            <StyleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="Grid view">
            <GridViewIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      <IconButton
        aria-label="Open settings"
        onClick={onOpenMenu}
        sx={{ color: "#e2e8f0", flexShrink: 0 }}>
        <MenuIcon />
      </IconButton>
    </header>
  );
};

export default StudyHeader;
